import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_AffinityGroupItemRow,
    WB_AffinityGroupRow,
    WB_AffinitySetRow,
    WB_EntityRow,
    WB_ModelCannibalizationTypeRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbAffinitySet} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_AffinityGroup,
    esWB_AffinityGroupItem,
    esWB_AffinitySet,
    esWB_Customer,
    esWB_Entity, esWB_Finished_Product,
    esWB_Model_Cannibalization_Type,
    esWB_Product_Group
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {BehaviorSubject} from "rxjs";
import {AffinitySetBOValidations} from "../PA.MD.BusinessObjects/AffinitySetBO.Validations"

export interface AffinitySetLoadNewOptions {
    CannibalizationTypeId: number,
    CustomerHierarchyId: number,
    CustomerLevelId: string,
    ProductHierarchyId: number,
    ProductLevelId: string,

    [other: string]: any
}

export class AffinitySetBO implements IBusinessObject {
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_AffinitySet: WB_AffinitySetRow;

    _OnAffinityGroupsChanged: BehaviorSubject<AffinitySetBO>
    _OnAffinityGroupItemsChanged: BehaviorSubject<AffinitySetBO>

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Affinity Set'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_AffinitySet = undefined

        this._OnAffinityGroupsChanged = new BehaviorSubject<AffinitySetBO>(this)
        this._OnAffinityGroupItemsChanged = new BehaviorSubject<AffinitySetBO>(this)
    }

    get WB_AffinitySet(): WB_AffinitySetRow {
        return this.drWB_AffinitySet;
    }

    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: AffinitySetLoadNewOptions): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Affinity Set");
        let createOpts = _.defaultsDeep({}, options)

        let returnValue: Promise<IBusinessObject> = this._OnLoadComplete = (async () => {

            try {
                self.LoadOptions = options || {};
                if ('dbContext' in self.LoadOptions)
                    self.dbContext = self.LoadOptions.dbContext;
                else
                    self.dbContext = new PAdbContext();

                if (self.dbContext == null) {
                    let sError = "Unable to determine dbContext for new Affinity Set.";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                // Create record using dbContext
                let newAffinitySetRow: WB_AffinitySetRow = <WB_AffinitySetRow>await self.dbContext.createRecordWithKey(esWB_AffinitySet)
                newAffinitySetRow.setDefaults();
                self.drWB_AffinitySet = newAffinitySetRow;
                self.dbContext.add(esWB_AffinitySet, newAffinitySetRow);
                self.Identity.setAll(self.WB_AffinitySet.cif.WB_AFFINITYSET_ID, self.WB_AffinitySet.cif.AFFINITYSET_ID, self.WB_AffinitySet.cif.AFFINITYSET_NAME);
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_AffinitySet, 'WB_AFFINITYSET_ID', 'AFFINITYSET_ID', 'AFFINITYSET_NAME');

                //-- Lookup Load Options
                let cannType: WB_ModelCannibalizationTypeRow = await self.dbContext.find(esWB_Model_Cannibalization_Type, options?.CannibalizationTypeId)
                self.drWB_AffinitySet.cif.WB_MODEL_CANNIBALIZATIONM_ID = cannType.cif.WB_MODEL_CANNIBALIZATIONM_ID
                self.drWB_AffinitySet.WB_ModelCannibalizationTypeRow = cannType

                self.drWB_AffinitySet.cif.WB_MDSET_ID = createOpts?.wbMasterDataSetId || 0
                self.drWB_AffinitySet.cif.WB_SALESORG_ID = createOpts?.wbSalesOrgId || 0
                self.drWB_AffinitySet.cif.WB_ENTITY_ID_CUSTOMER = createOpts?.wbCustomerTypeId || 0
                self.drWB_AffinitySet.cif.WB_CUSTH_ID = createOpts?.wbCustomerHierarchyId || 0
                self.drWB_AffinitySet.cif.WB_ENTITY_ID_PRODUCT = createOpts?.wbProductTypeId || 0
                self.drWB_AffinitySet.cif.WB_PRDH_ID = createOpts?.wbProductHierarchyId || 0
                self.drWB_AffinitySet.CUSTOMER_LEVEL = createOpts.customerLevel || ""
                self.drWB_AffinitySet.PRODUCT_LEVEL = createOpts.productLevel || ""

                if (_.includes(['FinishedProduct', 'ConsumerProduct'], self.drWB_AffinitySet.PRODUCT_LEVEL)){
                    self.drWB_AffinitySet.bItemHasProduct=true
                    self.drWB_AffinitySet.bItemHasProductGroup=false
                }else{
                    self.drWB_AffinitySet.bItemHasProduct=false
                    self.drWB_AffinitySet.bItemHasProductGroup=true
                }

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                return self
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                throw err
            }

        })();

        return returnValue;
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                let sError = "Affinity Set already loaded.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            self.LoadOptions = options || {};

            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null)
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for Affinity Set.";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = AffinitySetBO.createRecordResolver({dbContext: self.dbContext});
                recordResolverLoadPromise = AffinitySetBO.setupRecordResolver(recordResolver, [anId])
            }

            recordResolverLoadPromise
                .then(function () {
                    let newAffinitySetRow: any = recordResolver.get(esWB_AffinitySet, anId);

                    if (_.isEmpty(newAffinitySetRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_AffinitySet = newAffinitySetRow;
                    self.Identity.setAll(self.WB_AffinitySet.cif.WB_AFFINITYSET_ID, self.WB_AffinitySet.cif.AFFINITYSET_ID, self.WB_AffinitySet.cif.AFFINITYSET_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_AffinitySet, 'WB_AFFINITYSET_ID', 'AFFINITYSET_ID', 'AFFINITYSET_NAME');
                    return self.dbContext.findAll(esWB_Entity, {where: {WB_ENTITY_ID: {inq: [self.drWB_AffinitySet.cif.WB_ENTITY_ID_PRODUCT, self.drWB_AffinitySet.cif.WB_ENTITY_ID_CUSTOMER]}}})
                })
                .then((arrEntity: WB_EntityRow[]) => {
                    self.drWB_AffinitySet.CUSTOMER_LEVEL = _.first(_.filter(arrEntity, entity => entity.cif.WB_ENTITY_ID == self.drWB_AffinitySet.cif.WB_ENTITY_ID_CUSTOMER))?.cif.ENTITY_ID
                    self.drWB_AffinitySet.PRODUCT_LEVEL = _.first(_.filter(arrEntity, entity => entity.cif.WB_ENTITY_ID == self.drWB_AffinitySet.cif.WB_ENTITY_ID_PRODUCT))?.cif.ENTITY_ID
                    if (_.includes(['FinishedProduct', 'ConsumerProduct'], self.drWB_AffinitySet.PRODUCT_LEVEL)){
                        self.drWB_AffinitySet.bItemHasProduct=true
                        self.drWB_AffinitySet.bItemHasProductGroup=false
                    }else{
                        self.drWB_AffinitySet.bItemHasProduct=false
                        self.drWB_AffinitySet.bItemHasProductGroup=true
                    }
                })
                .then((newAffinitySetRow: any) => {
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err: any) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    error(err);
                });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {

        let self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_AffinitySet.cif.AFFINITYSET_NAME + "(" + self.WB_AffinitySet.cif.AFFINITYSET_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                let sError = "Unable to determine dbContext to Validate Affinity Set.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            AffinitySetBOValidations.ValidateOverlap(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })

            self._bIsValidated = true;
            self.ValidateProgressProvider.finish();
            resolve(self);
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        const self = this;
        const wbAffinitySet = <IwbAffinitySet>self.drWB_AffinitySet;
        this.SaveProgressProvider.start("Saving " + wbAffinitySet.AFFINITYSET_NAME + "(" + wbAffinitySet.AFFINITYSET_ID + ")");

        // Doesn't need to do anything yet
        // All the records are in the store, since they were added through find or createNewRecord.
        // We could search the tree if there is any record where store isn't set.

        if (self.dbContext == null) {
            self._bIsSaved = false;
            let sError = "Unable to determine dbContext to Save Affinity Set.";
            self.SaveProgressProvider.exception(sError);
            throw new Error(sError)
            return
        }


        self.SaveOptions = options || {};

        let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;
        if (bBatch) {
            saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
        } else {
            saveFunction = self.dbContext.Save.bind(self.dbContext);
        }

        try {

            await self.Validate({})
            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return
            }
            await saveFunction(esWB_AffinitySet, self.drWB_AffinitySet, {})
            await saveFunction(esWB_AffinityGroup, self.drWB_AffinitySet.WB_AffinityGroupRows, {})
            await saveFunction(esWB_AffinityGroupItem, _.flatMap(self.drWB_AffinitySet.WB_AffinityGroupRows, (a) => a.WB_AffinityGroupItemRows))

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                })
            } else {
                self._bIsSaved = true;
            }
            self.SaveProgressProvider.finish();
            return self
        } catch (err) {
            self.SaveProgressProvider.exception(err);
            throw err
        }
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    //Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const affinitySet = <IwbAffinitySet>self.drWB_AffinitySet;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + affinitySet.AFFINITYSET_NAME + "(" + affinitySet.AFFINITYSET_ID + ")");


        try {
            // 1. Validate if we can delete the Affinity Set

            // 2. If Valid, mark all the records for deletion.
            self.drWB_AffinitySet.markDeleted();

            _.forEach(self.WB_AffinitySet.WB_AffinityGroupRows, aGroup => {
                _.forEach(aGroup.WB_AffinityGroupItemRows, anItem => {
                    anItem.markDeleted()
                })
                aGroup.markDeleted()
            })

            if (bSaveNow) {
                await self.SaveUsing(false)

                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                return self
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                return self;
            }
        } catch (err) {
            this.DeleteProgressProvider.exception(err);
            return self
        }
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>


    //<editor-fold desc = "Add AffinityGroups operations">


    async addAffinityGroup(): Promise<WB_AffinityGroupRow> {
        const self = this

        let aNewRecord: WB_AffinityGroupRow = <WB_AffinityGroupRow>await self.dbContext.createRecordWithKey(esWB_AffinityGroup)
        aNewRecord.setDefaults()
        aNewRecord.cif.WB_AFFINITYSET_ID = self.drWB_AffinitySet.cif.WB_AFFINITYSET_ID
        aNewRecord.WB_AffinitySetRow = self.drWB_AffinitySet
        self.dbContext.add(esWB_AffinityGroup, aNewRecord)
        self.OnAffinityGroupsChanged.next(self)
        return aNewRecord
    }


    async removeAffinityGroup(wbAffinityGroupId: number): Promise<void> {
        const self = this

        _.forEach(self.drWB_AffinitySet.WB_AffinityGroupRows, (aRow) => {
            if (aRow.cif.WB_AFFINITYGROUP_ID == wbAffinityGroupId) {
                aRow.markDeleted()
            }
        })
        self.OnAffinityGroupsChanged.next(self)
        return
    }

    get OnAffinityGroupsChanged(): BehaviorSubject<AffinitySetBO> {
        return this._OnAffinityGroupsChanged
    }


    async addAffinityGroupItem(aGroupRow: WB_AffinityGroupRow, wbCustId: number, wbProdId: number): Promise<WB_AffinityGroupItemRow> {
        const self = this
        let bFound: boolean = false
        _.forEach(aGroupRow.WB_AffinityGroupItemRows, aRow => {
            if (aRow.isActive && aRow.cif.WB_CUST_ID == wbCustId && (self.WB_AffinitySet.bItemHasProductGroup ? aRow.cif.WB_PRDGRP_ID : aRow.cif.WB_PROD_ID) == wbProdId) {
                bFound = true
                return
            }
        })

        if (bFound) {
            return
        }


        let aNewRecord: WB_AffinityGroupItemRow = <WB_AffinityGroupItemRow>await self.dbContext.createRecordWithKey(esWB_AffinityGroupItem)
        aNewRecord.setDefaults()
        aNewRecord.cif.WB_AFFINITYSET_ID = self.drWB_AffinitySet.cif.WB_AFFINITYSET_ID
        aNewRecord.WB_AffinityGroupRow = aGroupRow
        aNewRecord.cif.WB_AFFINITYGROUP_ID = aGroupRow.cif.WB_AFFINITYGROUP_ID
        //customer
        aNewRecord.cif.WB_CUST_ID = wbCustId
        if (wbCustId) { // @ts-ignore
            aNewRecord.WB_CustomerRow = _.first(await self.dbContext.findUsingCache(esWB_Customer, [wbCustId]))
        }
        //product

        if (wbProdId) {
            if (self.WB_AffinitySet.bItemHasProductGroup) {
                aNewRecord.cif.WB_PRDGRP_ID = wbProdId
                // @ts-ignore
                aNewRecord.WB_ProductGroupRow = _.first(await self.dbContext.findUsingCache(esWB_Product_Group, [wbProdId]))
            } else {
                if (self.WB_AffinitySet.bItemHasProduct) {
                    aNewRecord.cif.WB_PROD_ID = wbProdId
                    // @ts-ignore
                    aNewRecord.WB_ProductGroupRow = _.first(await self.dbContext.findUsingCache(esWB_Finished_Product, [wbProdId]))

                }
            }
        }
        self.dbContext.add(esWB_AffinityGroupItem, aNewRecord)
        self.OnAffinityGroupItemsChanged.next(self)
        return aNewRecord

    }

    async removeAffinityGroupItem(wbAffinityGroupItemId: number): Promise<void> {
        const self = this

        _.forEach(self.drWB_AffinitySet.WB_AffinityGroupRows, (aRow) => {
            _.forEach(aRow.WB_AffinityGroupItemRows, anItemRow => {
                if (anItemRow.cif.WB_AFFINITYGROUPITEM_ID == wbAffinityGroupItemId) {
                    anItemRow.markDeleted()
                }
            })
        })
        self.OnAffinityGroupItemsChanged.next(self)
        return
    }

    get OnAffinityGroupItemsChanged(): BehaviorSubject<AffinitySetBO> {
        return this._OnAffinityGroupItemsChanged
    }

    //</editor-fold>


    //<editor-fold desc = "IRecordResolver operations">

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const affinityset_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        const aRecResolver = new ESRecordResolver(dbContext, affinityset_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_AffinitySet, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>

}

PAEnvironment.registerBusinessObjectClass(AffinitySetBO, "AffinitySetBO", "Affinity Set")